import * as React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, type MotionStyle, type Variants } from 'framer-motion';
import clsx from 'clsx';

/**
 * Positioning variant for `ModalShell`.
 * - `center`: Standard modal — backdrop + centered animated body that scales/fades in.
 * - `responsive-sheet`: Bottom sheet on mobile, centered on `sm+`. Slides up from the bottom.
 * - `bottom-drawer`: Always-bottom slide-up. No backdrop by default.
 */
export type ModalShellPosition = 'center' | 'responsive-sheet' | 'bottom-drawer';

export interface ModalShellProps {
  isOpen: boolean;
  /** Optional close handler. Wired into the backdrop click unless disabled. */
  onClose?: () => void;
  /** Visual layout. Default `'center'`. */
  position?: ModalShellPosition;
  /** Render via `createPortal` to `document.body`. Default `false` (in-tree). */
  portal?: boolean;
  /** Hide the dimmed backdrop entirely. Default `false`; forced `true` by `bottom-drawer`. */
  hideBackdrop?: boolean;
  /** Close when the backdrop is clicked. Default `true`. */
  closeOnBackdropClick?: boolean;
  /** Classes applied to the animated body container (size, rounding, colors). */
  bodyClassName?: string;
  /** Inline styles applied to the animated body container. */
  bodyStyle?: MotionStyle;
  /** Body content. */
  children: React.ReactNode;
}

const backdropVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const bodyVariantsByPosition: Record<ModalShellPosition, Variants> = {
  center: {
    hidden: { opacity: 0, scale: 0.95, y: 10 },
    visible: { opacity: 1, scale: 1, y: 0 },
  },
  'responsive-sheet': {
    hidden: { y: '100%', opacity: 0 },
    visible: { y: 0, opacity: 1 },
  },
  'bottom-drawer': {
    hidden: { y: '100%' },
    visible: { y: 0 },
  },
};

const wrapperClassByPosition: Record<ModalShellPosition, string> = {
  center: 'fixed inset-0 z-modal flex items-center justify-center p-4',
  'responsive-sheet': 'fixed inset-0 z-modal flex items-end justify-center sm:items-center',
  'bottom-drawer': 'fixed bottom-0 left-0 z-modal w-full',
};

const SPRING_TRANSITION = { type: 'spring', damping: 25, stiffness: 300 } as const;
const DRAWER_TRANSITION = { type: 'spring', damping: 25, stiffness: 200 } as const;

/**
 * Single modal scaffold collapsing the portal + backdrop + animated-body
 * pattern that previously lived in `Modal`, `GradientModal`, `BottomDrawer`,
 * and the three share sheets. Variants pick sensible animation + wrapper
 * defaults; callers only style the body via `bodyClassName` / `bodyStyle`.
 */
export const ModalShell: React.FC<ModalShellProps> = ({
  isOpen,
  onClose,
  position = 'center',
  portal = false,
  hideBackdrop = false,
  closeOnBackdropClick = true,
  bodyClassName,
  bodyStyle,
  children,
}) => {
  const showBackdrop = !hideBackdrop && position !== 'bottom-drawer';
  const bodyVariants = bodyVariantsByPosition[position];
  const transition = position === 'bottom-drawer' ? DRAWER_TRANSITION : SPRING_TRANSITION;

  const tree = (
    <AnimatePresence>
      {isOpen && (
        <div className={wrapperClassByPosition[position]}>
          {showBackdrop && (
            <motion.div
              className='absolute inset-0 bg-black/40 backdrop-blur-sm'
              variants={backdropVariants}
              initial='hidden'
              animate='visible'
              exit='hidden'
              onClick={closeOnBackdropClick && onClose ? onClose : undefined}
            />
          )}

          <motion.div
            className={clsx('relative', bodyClassName)}
            {...(bodyStyle !== undefined ? { style: bodyStyle } : {})}
            variants={bodyVariants}
            initial='hidden'
            animate='visible'
            exit='hidden'
            transition={transition}
          >
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  if (!portal) return tree;
  if (typeof document === 'undefined') return null;
  return createPortal(tree, document.body);
};

ModalShell.displayName = 'ModalShell';
